import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Plus, FileText, Trash2, Check, ChevronsUpDown, Download, Mail, Share2,
  Eye, Send, Loader2, Clock, CheckCircle, XCircle, ArrowRight
} from 'lucide-react';

export default function ProformaInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [items, setItems] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [createMode, setCreateMode] = useState('scratch'); // 'scratch' or 'sales_order'
  const [selectedSO, setSelectedSO] = useState('');

  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([]);
  const [currentLine, setCurrentLine] = useState({ item_id: '', quantity: 1, unit_price: 0 });
  const [itemSearchOpen, setItemSearchOpen] = useState(false);

  const invoiceRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invRes, itemsRes, soRes] = await Promise.all([
        api.get('/proforma-invoices'),
        api.get('/items'),
        api.get('/sales-orders')
      ]);
      setInvoices(invRes.data);
      setItems(itemsRes.data);
      setSalesOrders(soRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const addLine = () => {
    if (!currentLine.item_id || currentLine.quantity <= 0 || currentLine.unit_price <= 0) {
      toast.error('Please select item and fill quantity/price');
      return;
    }
    const item = items.find(i => i.id === currentLine.item_id);
    setLines([...lines, {
      item_id: currentLine.item_id,
      sku: item.sku,
      name: item.name,
      quantity: currentLine.quantity,
      unit_price: currentLine.unit_price,
      line_total: currentLine.quantity * currentLine.unit_price
    }]);
    setCurrentLine({ item_id: '', quantity: 1, unit_price: 0 });
  };

  const removeLine = (index) => setLines(lines.filter((_, i) => i !== index));

  const resetForm = () => {
    setCustomerName(''); setCustomerEmail(''); setCustomerPhone('');
    setCustomerAddress(''); setValidityDays(30); setTaxRate(0);
    setNotes(''); setLines([]); setSelectedSO(''); setCreateMode('scratch');
    setCurrentLine({ item_id: '', quantity: 1, unit_price: 0 });
  };

  const handleCreate = async () => {
    if (createMode === 'sales_order' && selectedSO) {
      try {
        await api.post('/proforma-invoices/from-sales-order', {
          sales_order_id: selectedSO,
          validity_days: validityDays,
          notes: notes,
          tax_rate: taxRate
        });
        toast.success('Proforma invoice created from Sales Order!');
        setCreateOpen(false); resetForm(); fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create');
      }
      return;
    }

    if (!customerName || lines.length === 0) {
      toast.error('Please enter customer name and add items');
      return;
    }
    try {
      await api.post('/proforma-invoices', {
        customer_name: customerName,
        customer_email: customerEmail || null,
        customer_phone: customerPhone || null,
        customer_address: customerAddress || null,
        lines: lines.map(l => ({ item_id: l.item_id, quantity: l.quantity, unit_price: l.unit_price })),
        validity_days: validityDays,
        notes: notes || null,
        tax_rate: taxRate
      });
      toast.success('Proforma invoice created!');
      setCreateOpen(false); resetForm(); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    }
  };

  const openPreview = async (invoiceId) => {
    try {
      const res = await api.get(`/proforma-invoices/${invoiceId}`);
      setPreviewInvoice(res.data);
      setPreviewOpen(true);
    } catch { toast.error('Failed to load invoice'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this proforma invoice?')) return;
    try {
      await api.delete(`/proforma-invoices/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const downloadPdf = async () => {
    if (!invoiceRef.current) return;
    setGeneratingPdf(true);
    try {
      const canvas = await html2canvas(invoiceRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${previewInvoice.invoice_number}.pdf`);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to generate PDF'); }
    finally { setGeneratingPdf(false); }
  };

  const shareWhatsApp = async () => {
    if (!invoiceRef.current) return;
    try {
      const canvas = await html2canvas(invoiceRef.current, { scale: 2, backgroundColor: '#ffffff' });
      canvas.toBlob(async (blob) => {
        if (navigator.share) {
          const file = new File([blob], `${previewInvoice.invoice_number}.png`, { type: 'image/png' });
          await navigator.share({ files: [file], title: previewInvoice.invoice_number });
        } else {
          const url = canvas.toDataURL();
          const link = document.createElement('a');
          link.download = `${previewInvoice.invoice_number}.png`;
          link.href = url;
          link.click();
          toast.success('Image downloaded — share it on WhatsApp');
        }
      }, 'image/png');
    } catch { toast.error('Failed to share'); }
  };

  const sendEmail = async () => {
    if (!emailTo || !previewInvoice) return;
    setSendingEmail(true);
    try {
      await api.post(`/proforma-invoices/${previewInvoice.id}/send-email?email=${encodeURIComponent(emailTo)}`);
      toast.success(`Sent to ${emailTo}`);
      setEmailDialogOpen(false); setEmailTo('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally { setSendingEmail(false); }
  };

  const fmt = (v) => new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', minimumFractionDigits: 2 }).format(v).replace('JMD', 'J$');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const subtotal = lines.reduce((s, l) => s + l.line_total, 0);

  const statusColors = {
    draft: 'bg-[#8E8E93]/10 text-[#8E8E93]',
    sent: 'bg-[#007AFF]/10 text-[#007AFF]',
    accepted: 'bg-[#34C759]/10 text-[#34C759]',
    rejected: 'bg-[#FF3B30]/10 text-[#FF3B30]',
    expired: 'bg-[#FF9500]/10 text-[#FF9500]'
  };

  return (
    <div className="space-y-6" data-testid="proforma-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">PROFORMA INVOICES</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Create and manage proforma invoices</p>
        </div>
        <Button className="bg-[#007AFF] hover:bg-[#0066CC]" onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="new-proforma-btn">
          <Plus className="w-4 h-4 mr-2" />New Proforma
        </Button>
      </div>

      {/* Invoices List */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#007AFF]" />Proforma Invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <FileText className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No proforma invoices yet</p>
              <p className="text-[#0A0A0A]/40 text-sm mt-1">Create one to send price quotes to customers</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">Invoice #</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Customer</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-center">Items</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Total</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Valid Until</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="border-b border-[#E5E5EA]">
                    <TableCell className="font-mono font-medium">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.customer_name}</TableCell>
                    <TableCell className="text-center">{inv.lines?.length || 0}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(inv.total)}</TableCell>
                    <TableCell><Badge className={statusColors[inv.status] || statusColors.draft}>{inv.status}</Badge></TableCell>
                    <TableCell>{fmtDate(inv.valid_until)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openPreview(inv.id)} data-testid={`preview-pi-${inv.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-[#FF3B30]" onClick={() => handleDelete(inv.id)} data-testid={`delete-pi-${inv.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#007AFF]" />New Proforma Invoice
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Mode selector */}
            <div className="flex gap-2">
              <Button variant={createMode === 'scratch' ? 'default' : 'outline'} size="sm" onClick={() => setCreateMode('scratch')} data-testid="mode-scratch">
                Create from Scratch
              </Button>
              <Button variant={createMode === 'sales_order' ? 'default' : 'outline'} size="sm" onClick={() => setCreateMode('sales_order')} data-testid="mode-so">
                From Sales Order
              </Button>
            </div>

            {createMode === 'sales_order' ? (
              <div className="space-y-2">
                <Label>Select Sales Order</Label>
                <Select value={selectedSO} onValueChange={setSelectedSO}>
                  <SelectTrigger><SelectValue placeholder="Select a sales order" /></SelectTrigger>
                  <SelectContent>
                    {salesOrders.map((so) => (
                      <SelectItem key={so.id} value={so.id}>{so.so_number} — {so.customer_name || 'No customer'} ({fmt(so.subtotal)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                {/* Customer Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Name *</Label>
                    <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" data-testid="pi-customer-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="customer@email.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="876-xxx-xxxx" />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Address" />
                  </div>
                </div>

                {/* Add Items */}
                <Card className="border border-dashed border-[#007AFF]/30 bg-[#007AFF]/5">
                  <CardContent className="p-4">
                    <Label className="text-sm font-medium mb-3 block">Add Items</Label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-5">
                        <Popover open={itemSearchOpen} onOpenChange={setItemSearchOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="pi-item-search-btn">
                              {currentLine.item_id ? (() => { const it = items.find(i => i.id === currentLine.item_id); return it ? `${it.sku} - ${it.name}` : 'Select'; })() : 'Search items...'}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[320px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search by name or SKU..." data-testid="pi-item-search-input" />
                              <CommandList>
                                <CommandEmpty>No items found.</CommandEmpty>
                                <CommandGroup>
                                  {items.map((item) => (
                                    <CommandItem key={item.id} value={`${item.sku} ${item.name}`} onSelect={() => {
                                      setCurrentLine({ ...currentLine, item_id: item.id, unit_price: item.selling_price || item.avg_cost || 0 });
                                      setItemSearchOpen(false);
                                    }}>
                                      <Check className={`mr-2 h-4 w-4 ${currentLine.item_id === item.id ? 'opacity-100' : 'opacity-0'}`} />
                                      <span className="font-mono text-xs mr-2">{item.sku}</span>
                                      <span className="truncate">{item.name}</span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="Qty" value={currentLine.quantity} onChange={(e) => setCurrentLine({ ...currentLine, quantity: parseFloat(e.target.value) || 1 })} min="1" />
                      </div>
                      <div className="col-span-3">
                        <Input type="number" placeholder="Price" value={currentLine.unit_price} onChange={(e) => setCurrentLine({ ...currentLine, unit_price: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
                      </div>
                      <div className="col-span-2">
                        <Button type="button" className="w-full bg-[#007AFF]" onClick={addLine} data-testid="pi-add-line-btn"><Plus className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Line items table */}
                {lines.length > 0 && (
                  <Card className="border border-[#E5E5EA]">
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow><TableHead className="text-xs">Item</TableHead><TableHead className="text-xs text-right">Qty</TableHead><TableHead className="text-xs text-right">Price</TableHead><TableHead className="text-xs text-right">Total</TableHead><TableHead className="text-xs w-10"></TableHead></TableRow>
                        </TableHeader>
                        <TableBody>
                          {lines.map((line, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="py-2"><p className="font-medium text-sm">{line.name}</p><p className="text-xs text-[#0A0A0A]/50">{line.sku}</p></TableCell>
                              <TableCell className="text-right font-mono text-sm">{line.quantity}</TableCell>
                              <TableCell className="text-right font-mono text-sm">{fmt(line.unit_price)}</TableCell>
                              <TableCell className="text-right font-mono text-sm font-medium">{fmt(line.line_total)}</TableCell>
                              <TableCell><Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="text-[#FF3B30] h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <div className="p-4 border-t border-[#E5E5EA] flex justify-between">
                        <span className="font-medium">Subtotal:</span>
                        <span className="font-mono font-bold">{fmt(subtotal)}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Common fields */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Valid for (days)</Label>
                <Input type="number" value={validityDays} onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)} min="1" />
              </div>
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input type="number" value={taxRate} onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)} min="0" step="0.5" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, conditions..." rows={2} />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#007AFF]" disabled={createMode === 'scratch' ? lines.length === 0 : !selectedSO} data-testid="create-pi-btn">
              Create Proforma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#007AFF]" />
              {previewInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>

          {previewInvoice && (
            <>
              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button size="sm" variant="outline" onClick={downloadPdf} disabled={generatingPdf} data-testid="download-pdf-btn">
                  {generatingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  Download PDF
                </Button>
                <Button size="sm" variant="outline" onClick={shareWhatsApp} data-testid="share-whatsapp-btn">
                  <Share2 className="w-4 h-4 mr-1" />WhatsApp
                </Button>
                <Button size="sm" className="bg-[#007AFF]" onClick={() => { setEmailTo(previewInvoice.customer_email || ''); setEmailDialogOpen(true); }} data-testid="send-email-btn">
                  <Mail className="w-4 h-4 mr-1" />Send Email
                </Button>
              </div>

              {/* Invoice Preview (this gets rendered to PDF/image) */}
              <div ref={invoiceRef} className="bg-white p-8 border border-[#E5E5EA] rounded-lg" data-testid="invoice-preview">
                {/* Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h2 className="text-2xl font-black text-[#0A0A0A] tracking-tight">PROFORMA INVOICE</h2>
                    <p className="text-sm text-[#0A0A0A]/50 font-mono mt-1">{previewInvoice.invoice_number}</p>
                  </div>
                  <Badge className={statusColors[previewInvoice.status] || statusColors.draft} data-testid="invoice-status">
                    {previewInvoice.status}
                  </Badge>
                </div>

                {/* Business & Customer Info */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                  <div>
                    <p className="text-xs font-semibold text-[#007AFF] uppercase tracking-wider mb-2">From</p>
                    <p className="font-bold text-[#0A0A0A]">{previewInvoice.business_name}</p>
                    {previewInvoice.business_address && <p className="text-sm text-[#0A0A0A]/60">{previewInvoice.business_address}</p>}
                    {previewInvoice.business_phone && <p className="text-sm text-[#0A0A0A]/60">{previewInvoice.business_phone}</p>}
                    {previewInvoice.business_email && <p className="text-sm text-[#0A0A0A]/60">{previewInvoice.business_email}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-[#007AFF] uppercase tracking-wider mb-2">To</p>
                    <p className="font-bold text-[#0A0A0A]">{previewInvoice.customer_name}</p>
                    {previewInvoice.customer_address && <p className="text-sm text-[#0A0A0A]/60">{previewInvoice.customer_address}</p>}
                    {previewInvoice.customer_phone && <p className="text-sm text-[#0A0A0A]/60">{previewInvoice.customer_phone}</p>}
                    {previewInvoice.customer_email && <p className="text-sm text-[#0A0A0A]/60">{previewInvoice.customer_email}</p>}
                  </div>
                </div>

                {/* Dates */}
                <div className="flex gap-6 mb-6 p-3 bg-[#F5F5F7] rounded-lg text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#0A0A0A]/40" />
                    <span className="text-[#0A0A0A]/60">Date:</span>
                    <span className="font-medium">{fmtDate(previewInvoice.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-[#FF9500]" />
                    <span className="text-[#0A0A0A]/60">Valid Until:</span>
                    <span className="font-medium text-[#FF9500]">{fmtDate(previewInvoice.valid_until)}</span>
                  </div>
                </div>

                {/* Items Table */}
                <table className="w-full text-sm mb-6">
                  <thead>
                    <tr className="border-b-2 border-[#0A0A0A]">
                      <th className="text-left py-2 font-semibold">Item</th>
                      <th className="text-left py-2 font-semibold">SKU</th>
                      <th className="text-center py-2 font-semibold">Qty</th>
                      <th className="text-right py-2 font-semibold">Price</th>
                      <th className="text-right py-2 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewInvoice.lines?.map((line, idx) => (
                      <tr key={idx} className="border-b border-[#E5E5EA]">
                        <td className="py-3">{line.name}</td>
                        <td className="py-3 font-mono text-xs text-[#0A0A0A]/60">{line.sku}</td>
                        <td className="py-3 text-center">{line.quantity}</td>
                        <td className="py-3 text-right font-mono">{fmt(line.unit_price)}</td>
                        <td className="py-3 text-right font-mono font-medium">{fmt(line.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#0A0A0A]/60">Subtotal</span>
                      <span className="font-mono">{fmt(previewInvoice.subtotal)}</span>
                    </div>
                    {previewInvoice.tax_rate > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#0A0A0A]/60">Tax ({previewInvoice.tax_rate}%)</span>
                        <span className="font-mono">{fmt(previewInvoice.tax_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t-2 border-[#0A0A0A] pt-2">
                      <span>Total</span>
                      <span className="font-mono">{fmt(previewInvoice.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {previewInvoice.notes && (
                  <div className="mt-6 p-4 bg-[#FFF8E1] rounded-lg border-l-4 border-[#FFD700]">
                    <p className="text-sm"><span className="font-semibold">Notes:</span> {previewInvoice.notes}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-8 pt-4 border-t border-[#E5E5EA] text-center text-xs text-[#0A0A0A]/40">
                  <p>This is a proforma invoice and not a demand for payment. Valid for {previewInvoice.validity_days} days.</p>
                  <p className="mt-1">Generated by JamInventra</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#007AFF]" />Send via Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Recipient Email</Label>
            <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="customer@email.com" data-testid="email-recipient-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#007AFF]" onClick={sendEmail} disabled={sendingEmail || !emailTo} data-testid="send-email-confirm-btn">
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
